import { createSelector, Selector } from '@ngxs/store';
import {
  ContactsPageRights,
  Plants,
  SlotCalendarRights,
  SlotDocumentsRights,
  SlotShipmentsRights,
} from 'apps/orion/src/app/features/scm/material-management/v1/types';
import { Plant } from '../configuration';
import {
  CarriersModuleRights,
  CommitsModuleRights,
  ConfigurationRights,
  DashboardsRights,
  DateTypeRights,
  FinancialModuleRights,
  GenerateInstructionsPageRights,
  PortfolioAdditionRights,
  PortfolioRights,
  ShipmentsModuleRights,
  SlotsModuleRights,
  SlotsStorageModuleRights,
  StocksModuleRights,
  SupplyVisibilityRights,
  UploadDocumentsPageRights,
  VendorListRights,
} from './authenticated-user.model';
import { AuthenticationState } from './authentication.state';
import { AuthenticationStateModel } from './authentication.types';

export class AuthenticationSelectors {
  @Selector([AuthenticationState])
  static slotDocumentRights({
    slotsModuleRights,
  }: AuthenticationStateModel): SlotDocumentsRights | undefined {
    return slotsModuleRights?.documents;
  }

  @Selector([AuthenticationState])
  static slotModuleRights({ slotsModuleRights }: AuthenticationStateModel): SlotsModuleRights {
    return slotsModuleRights as SlotsModuleRights;
  }

  @Selector([AuthenticationState])
  static contactsRights({
    contactsRights,
  }: AuthenticationStateModel): ContactsPageRights | undefined {
    return contactsRights;
  }

  @Selector([AuthenticationSelectors.activePlant])
  static isMultiPlant(activePlantCode: string): boolean {
    return activePlantCode === Plants.MULTI;
  }

  @Selector([AuthenticationState])
  static plantsWithoutMulti({ plants }: AuthenticationStateModel): Plant[] {
    return plants.filter((plant): boolean => plant.Code !== Plants.MULTI);
  }

  @Selector([AuthenticationState])
  static plants({ plants }: AuthenticationStateModel): Plant[] {
    return plants;
  }

  @Selector([AuthenticationState])
  static getLoading({ loading }: AuthenticationStateModel): boolean {
    return loading;
  }

  @Selector([AuthenticationState])
  static getCurrentRole({ currentRole }: AuthenticationStateModel): string | undefined {
    return currentRole;
  }

  @Selector([AuthenticationState])
  static getCurrentRoles({ currentRoles }: AuthenticationStateModel): string[] | undefined {
    return currentRoles;
  }

  @Selector([AuthenticationState])
  static token({ token }: AuthenticationStateModel): string {
    return token;
  }

  @Selector([AuthenticationState])
  static plant({ plant }: AuthenticationStateModel): unknown {
    return createSelector([AuthenticationState], (state: AuthenticationStateModel): unknown => {
      return state.plants.find((p): boolean => p.Code === plant);
    });
  }

  @Selector([AuthenticationState])
  static activePlant({ plant, plants }: AuthenticationStateModel): string {
    // TODO Remove this hack after visualizator is no longer used
    const plantCode = typeof plant === 'object' ? plant?.Code : plant;
    const storedPlant = plants.find((p): boolean => p.Code === plantCode);
    if (storedPlant) {
      return storedPlant.Code;
    }
    return plantCode as string;
  }

  @Selector([AuthenticationState])
  static getActiveplant({ plant }: AuthenticationStateModel): Plant | string | undefined {
    // VISUALIZATOR HACK, REMOVE AFTER VISUALIZATOR IS NOT USED
    const plantType: 'configurator' | 'visualizator' =
      typeof plant === 'object' ? 'visualizator' : 'configurator';

    if (plantType === 'visualizator') {
      return (plant as Plant).Code;
    }

    if (plantType === 'configurator') {
      return plant;
    }

    return;
  }

  @Selector([AuthenticationState])
  static isLoggedIn({ isLoggedIn }: AuthenticationStateModel): boolean {
    return isLoggedIn;
  }

  @Selector([AuthenticationState])
  static tenants({ tenants }: AuthenticationStateModel): string[] {
    return tenants;
  }

  @Selector([AuthenticationState])
  static activeTenant({ activeTenant }: AuthenticationStateModel): string {
    return activeTenant;
  }

  @Selector([AuthenticationState])
  static username({ username }: AuthenticationStateModel): string {
    return username;
  }

  @Selector([AuthenticationState])
  static upn({ upn }: AuthenticationStateModel): string {
    return upn;
  }

  @Selector([AuthenticationState])
  static primarySid({ primarySid }: AuthenticationStateModel): string {
    return primarySid;
  }

  @Selector([AuthenticationState])
  static commitModuleRights({
    commitsModuleRights,
  }: AuthenticationStateModel): CommitsModuleRights | undefined {
    return commitsModuleRights;
  }

  @Selector([AuthenticationState])
  static configurationRights({
    configurationRights,
  }: AuthenticationStateModel): ConfigurationRights | undefined {
    return configurationRights;
  }

  @Selector([AuthenticationState])
  static portfolioRights({
    portfolioRights,
  }: AuthenticationStateModel): PortfolioRights | undefined {
    return portfolioRights;
  }

  @Selector([AuthenticationState])
  static portfolioAdditionRights({
    portfolioRights,
  }: AuthenticationStateModel): PortfolioAdditionRights | undefined {
    return portfolioRights?.addition;
  }

  @Selector([AuthenticationState])
  static dashboardRights({
    dashboardRights,
  }: AuthenticationStateModel): DashboardsRights | undefined {
    return dashboardRights;
  }

  @Selector([AuthenticationState])
  static vendorListRights({
    vendorListRights,
  }: AuthenticationStateModel): undefined | VendorListRights {
    return vendorListRights;
  }

  @Selector([AuthenticationState])
  static supplyVisibilityRights({
    supplyVisibilityRights,
  }: AuthenticationStateModel): SupplyVisibilityRights | undefined {
    return supplyVisibilityRights;
  }

  @Selector([AuthenticationState])
  static finantialModuleRights({
    finantialModuleRights,
  }: AuthenticationStateModel): FinancialModuleRights | undefined {
    return finantialModuleRights;
  }

  @Selector([AuthenticationState])
  static generateInstructionsRights({
    generateInstructionsRights,
  }: AuthenticationStateModel): GenerateInstructionsPageRights | undefined {
    return generateInstructionsRights;
  }

  @Selector([AuthenticationState])
  static dateTypeRights({ dateTypeRights }: AuthenticationStateModel): DateTypeRights | undefined {
    return dateTypeRights;
  }

  @Selector([AuthenticationState])
  static dateTypeRightList({ dateTypeRightList }: AuthenticationStateModel): string[] | undefined {
    return dateTypeRightList;
  }

  @Selector([AuthenticationState])
  static menuAccessRightList({
    menuAccessRightList,
  }: AuthenticationStateModel): string[] | undefined {
    return menuAccessRightList;
  }

  @Selector([AuthenticationState])
  static widgetAccessRightList({
    widgetAccessRightList,
  }: AuthenticationStateModel): string[] | undefined {
    return widgetAccessRightList;
  }

  @Selector([AuthenticationState])
  static carriersModuleRights({
    carriersModuleRights,
  }: AuthenticationStateModel): CarriersModuleRights | undefined {
    return carriersModuleRights;
  }

  @Selector([AuthenticationState])
  static stocksModuleRights({
    stocksModuleRights,
  }: AuthenticationStateModel): StocksModuleRights | undefined {
    return stocksModuleRights;
  }

  @Selector([AuthenticationState])
  static slotsStorageModuleRights({
    slotsStorageModuleRights,
  }: AuthenticationStateModel): SlotsStorageModuleRights | undefined {
    return slotsStorageModuleRights;
  }

  @Selector([AuthenticationState])
  static slotsModuleRights({
    slotsModuleRights,
  }: AuthenticationStateModel): SlotsModuleRights | undefined {
    return slotsModuleRights;
  }

  @Selector([AuthenticationState])
  static slotsCalendarRights({
    slotsCalendarRights,
  }: AuthenticationStateModel): SlotCalendarRights | undefined {
    return slotsCalendarRights;
  }

  @Selector([AuthenticationState])
  static statusRightList({ statusRightList }: AuthenticationStateModel): string[] | undefined {
    return statusRightList;
  }

  @Selector([AuthenticationState])
  static supplyVisibilityModuleRights({
    supplyVisibilityRights,
  }: AuthenticationStateModel): SupplyVisibilityRights | undefined {
    return supplyVisibilityRights;
  }

  @Selector([AuthenticationState])
  static uploadDocumentsRights({
    uploadDocumentsRights,
  }: AuthenticationStateModel): undefined | UploadDocumentsPageRights {
    return uploadDocumentsRights;
  }

  @Selector([AuthenticationState])
  static slotShipmentsRights({
    slotsModuleRights,
  }: AuthenticationStateModel): SlotShipmentsRights | undefined {
    return slotsModuleRights?.shipments;
  }

  @Selector([AuthenticationState])
  static shipmentsModuleRights({
    shipmentsModuleRights,
  }: AuthenticationStateModel): ShipmentsModuleRights | undefined {
    return shipmentsModuleRights;
  }
}
